import useStaticSite from 'hooks/useStaticSite';

import type { ContentfulMetaSeo } from 'graphqlTypes';
import type { FC } from 'react';

type SeoProps = Pick<
  ContentfulMetaSeo & {
    slug?: string;
  },
  'canonicalUrl' | 'indexable' | 'openGraphImage' | 'pageDescription' | 'pageTitle' | 'slug'
>;

const SEO: FC<SeoProps> = ({
  canonicalUrl,
  indexable,
  openGraphImage,
  pageDescription,
  pageTitle,
  slug,
  productSchema,
}) => {
  const { site } = useStaticSite();

  const {
    siteMetadata: { defaultTitle, defaultDescription, ogLanguage, twitter, facebook, siteUrl },
  } = site;

  const seo = {
    title: pageTitle || defaultTitle,
    description: pageDescription || defaultDescription,
    imgUrl: openGraphImage?.file?.url || '',
    url: canonicalUrl || `${siteUrl}${slug?.startsWith('/') ? slug : `/${slug || ''}`}`,
  };

  return (
    <>
      <title>{`${seo.title} | Aspire`}</title>
      {seo.title && <meta name="title" content={`${seo.title} | Aspire`} />}
      {seo.description && <meta name="description" content={seo.description} />}
      <meta name="robots" content={!indexable ? 'noindex, nofollow' : 'index, follow'} />
      {seo.url && <link rel="canonical" href={seo.url} />}
      {facebook && <meta property="og:site_name" content={facebook} />}
      <meta property="og:locale" content={ogLanguage} />
      <meta property="og:url" content={seo.url} />
      <meta property="og:title" content={seo.title} />
      <meta property="og:description" content={seo.description} />
      <meta property="og:image" content={seo.imgUrl} />
      <meta property="og:image:alt" content={seo.description} />
      {productSchema && typeof productSchema === 'object' && (
        <script type="application/ld+json">{JSON.stringify(productSchema).replace(/_/g, '@')}</script>
      )}
      {twitter && <meta name="twitter:creator" content={twitter} />}
      <meta name="twitter:title" content={seo.title} />
      <meta name="twitter:description" content={seo.description} />
      <meta name="twitter:image" content={seo.imgUrl} />
      <meta name="twitter:image:alt" content={seo.description} />
    </>
  );
};

export default SEO;
